import { carryPendingCoverCropData, clearPendingCoverImage, clearPendingImages, isSamePendingCover, loadPendingCoverImage } from './pendingImageStorage';
import { fetchOrderedInteriorImages } from './bookPageOrderUtils';
import { mapDbCoverToGridImage } from './stagingBookImageUtils';
import { isStagingUploadEnabled } from '../services/mediaService';
import { safeStepDestination } from './safeStepRouteName';
// Static, not import(): with the admin pages split out nothing else keeps it in main.js, and a
// separate chunk would add a round trip before the profile and books fetch at every login.
import { profileDetails } from '../services/ProfileService';

/**
 * True when the customer should stay in the book funnel, i.e. the book has never been ordered.
 * Ordering is one-way (`isOrderPlaces` is never unset), so an ordered book is permanently
 * read-only: its uploads are deleted 14 days on, leaving the wizard nothing to edit. A re-order
 * sitting in the cart does not reopen the funnel — it checks out from the cart instead.
 * Used by both smart redirect and initial Redux hydration.
 */
export const hasBookWorkInProgress = (book) => !!book && !book.isOrderPlaces;

/**
 * Determines the appropriate destination route for a user based on their profile and books
 * @param {Object} profile - User profile data
 * @param {Array} myBooks - Array of user's books
 * @param {string} activeBookId - Currently active book ID
 * @param {Object} [options]
 * @param {Set<string>} [options.cartBookIds] - Books in the customer's cart. When the book being
 *   resumed is one of them, the customer already finished it and chose to order it, so they land
 *   on Finalize Order instead of that book's Review page. Omitted (Etsy) = no cart routing.
 * @returns {string} - The route to navigate to with query parameters
 */
export const determineUserDestination = (profile, myBooks = [], activeBookId = null, { cartBookIds } = {}) => {
  // No books - go to create book
  if (!myBooks.length) {
    return '/create-book?stepCode=Create_New_Book';
  }

  // If activeBookId exists, check if that book has work in progress
  if (!!activeBookId) {
    const activeBook = myBooks.find(book => book._id === activeBookId);

    // If active book is fully ordered (no work in progress), go to dashboard
    if (activeBook && !hasBookWorkInProgress(activeBook)) {
      return '/dashboard';
    }
  }

  // No active book - go to dashboard
  if (!activeBookId) {
    return '/dashboard';
  }

  if (cartBookIds?.has(String(activeBookId))) {
    return '/finalize-order';
  }

  // Has books with work in progress - find latest step
  const latestStep = fetchLatestStepCode(profile?.bookData, activeBookId);

  // If on Place_Order step, go to review
  if (latestStep?.code === 'Place_Order') {
    return '/review?stepCode=Review_Your_Book';
  }

  // If has a specific step with route, go there
  if (latestStep?.routeName && latestStep?.code) {
    return safeStepDestination(latestStep.routeName, latestStep.code);
  }

  // Default to dashboard
  return '/dashboard';
};

/**
 * Fetches the latest step code for a specific book
 * @param {Array} bookData - Array of book data from profile
 * @param {string} activeBookId - The active book ID
 * @returns {Object|null} - Latest step object or null
 */
export const fetchLatestStepCode = (bookData = [], activeBookId = null) => {
  const activeBook = bookData.find((book) => book._id === activeBookId);
  if (!activeBook) return null;

  const latest = activeBook.latestStepCode;
  const next = activeBook.nextStepCode;

  // If latest is completed, move to next. Otherwise stay on latest.
  if (typeof latest === 'object' && latest?.isCompleted === 'yes' && next) {
    return next;
  }
  return latest ?? null;
};

/**
 * The book named by `?bookId=` in the address bar, when it is one this customer
 * can actually resume. Abandoned-recovery emails deep-link to a specific book;
 * anything unrecognised is ignored so a bad or stale id just falls back to the
 * normal active-book selection rather than stranding the customer.
 * @param {Array} myBooks - the user's books (already scoped to them by the API)
 * @returns {string|null} - the requested book id, or null
 */
export const requestedBookId = (myBooks = []) => {
  if (typeof window === 'undefined') return null;
  const requested = new URLSearchParams(window.location.search).get('bookId');
  if (!requested) return null;
  const book = myBooks.find((b) => b._id === requested);
  return book && hasBookWorkInProgress(book) ? book._id : null;
};

const USER_DATA_ATTEMPTS = 3;
// Short gaps: the browser has already retried instantly by the time we see the failure, so the
// wait is just for a waking phone's connection to come back.
const USER_DATA_RETRY_DELAY_MS = (attempt) => (attempt === 1 ? 500 : 1500);
/** Pause before giving up, so callers that reload on failure (UserRoute) don't spin tightly. */
const USER_DATA_GIVE_UP_PAUSE_MS = 2000;

/**
 * No response at all (a lost request), or a server error. A 4xx such as an invalid token is
 * not transient: retrying won't help and the auth handling deals with it.
 */
const isTransientFailure = (body) =>
  !body ||
  body.status >= 500 ||
  (body.success === false && !(body.status >= 400 && body.status < 500));

/**
 * Fetches profile + books, retrying transient failures.
 *
 * A lost request must never be read as "this customer has no books": that routes them to a blank
 * create-book form and they start a duplicate of the book they were building. Seen on iPhones
 * reloading a backgrounded tab, where the requests never reached the server.
 *
 * Throws once retries are exhausted, so callers keep books "loading" instead of showing none.
 */
const fetchProfileAndBooks = async (userId, { profileDetails, booksGet, takeLastNetworkError }) => {
  const { withRetries } = await import('./retry');
  let diagnostics;

  // apiRequest usually resolves a lost request to `undefined`, but a caller that joins an
  // identical in-flight request gets the raw rejection instead. Handle both the same way.
  const settle = (promise) => promise.then((body) => ({ body }), (error) => ({ error }));

  const attempt = async () => {
    const [profile, books] = await Promise.all([
      settle(profileDetails(userId)),
      settle(booksGet(diagnostics)),
    ]);
    const profileRes = profile.body;
    const bookRes = books.body;

    const profileFailed = !!profile.error || isTransientFailure(profileRes);
    // The list's own success flag sits under `docs`; a database error still arrives as HTTP 200.
    const booksFailed =
      !!books.error ||
      isTransientFailure(bookRes) ||
      (!!bookRes?.docs && isTransientFailure(bookRes.docs));

    if (!profileFailed && !booksFailed) return { profileRes, bookRes };

    const describe = ({ error }, body, method, url) =>
      (error && `${error.code || "NO_CODE"}: ${error.message || String(error)}`) ||
      takeLastNetworkError(method, url) ||
      (body ? `HTTP ${body.status ?? "?"}` : "no response");
    const reason = [
      profileFailed && `profile ${describe(profile, profileRes, "get", "/profile/detail")}`,
      booksFailed && `books ${describe(books, bookRes?.docs ?? bookRes, "get", "book/list")}`,
    ]
      .filter(Boolean)
      .join("; ");
    throw new Error(reason);
  };

  try {
    return await withRetries(attempt, {
      attempts: USER_DATA_ATTEMPTS,
      delayMs: USER_DATA_RETRY_DELAY_MS,
      onRetry: (err, attemptNumber) => {
        console.warn(`[USER-DATA-RETRY] attempt ${attemptNumber} failed: ${err.message}`);
        // The failed request left no trace on the server, so the retried book-list request
        // carries the reason; it shows up in the nginx access log (grep "clientRetry").
        diagnostics = { clientRetry: String(attemptNumber), clientError: err.message.slice(0, 200) };
      },
    });
  } catch (err) {
    console.warn(`[USER-DATA-RETRY] giving up: ${err.message}`);
    await new Promise((resolve) => setTimeout(resolve, USER_DATA_GIVE_UP_PAUSE_MS));
    throw new Error(`Could not load user data: ${err.message}`);
  }
};

/**
 * Loads all necessary user data (profile, books, images) and prepares Redux state
 * @param {string} userId - User ID
 * @param {Function} dispatch - Redux dispatch function
 * @param {boolean} isComingFromNonUserRoute - Flag to skip certain operations
 * @returns {Promise<Object>} - Returns { profile, myBooks, activeBookId }
 * @throws when profile/books still can't be loaded after retries (books stay "loading")
 */
export const loadUserData = async (userId, dispatch, isComingFromNonUserRoute = false) => {
  const { booksGet } = await import('../services/bookService');
  const { takeLastNetworkError } = await import('./apiUtils');
  const { fetchBatchImagesByBookId } = await import('../services/mediaService');
  const { setMyBooks, setProfile, setBooksLoading, setBooksLoaded } = await import('../redux/slice/profileSlice');
  const { setActiveBookId } = await import('../redux/slice/stepSlice');
  const { setCoverImages, setImages } = await import('../redux/slice/sketchSlice');
  const { loadPendingImages, loadPendingCoverImage } = await import('./pendingImageStorage');

  // Taken before the request: a book created elsewhere while it is in flight may be missing from
  // the list, so cart lines newer than this aren't pruned for being unknown.
  const loadStartedAt = Date.now();

  // Fetch profile and books in parallel
  const { profileRes, bookRes } = await fetchProfileAndBooks(userId, {
    profileDetails,
    booksGet,
    takeLastNetworkError,
  });

  const profile = profileRes?.data || null;
  const profileBooks = profile?.bookData || [];

  let myBooks = [];
  let activeBookId = null;

  if (bookRes?.docs?.data?.length) {
    // Merge books with profile data
    myBooks = bookRes.docs.data.map(item => {
      const profileBook = profileBooks.find(({ _id }) => item._id === _id);
      const isOrderPlaces = profileBook?.isOrderPlaces;
      const completedStepData = profileBook?.completedSteps || [];
      const completedSteps = completedStepData
        .filter(step => step?.isCompleted === 'yes')
        .map(step => step.code);

      return {
        ...item,
        isOrderPlaces: !!isOrderPlaces,
        completedSteps
      };
    });

    dispatch(setMyBooks(myBooks));

    // Determine active book. An explicit ?bookId wins: recovery emails link to
    // one specific book, and without this the param is ignored and the customer
    // lands on whatever latestBookId happens to point at. Only honoured when the
    // id is one of this user's own books (myBooks is server-scoped to them) and
    // still has work in progress — a since-ordered book falls through.
    let targetBookId = requestedBookId(myBooks);

    if (!targetBookId) targetBookId = profile.latestBookId ?? null;

    if (!targetBookId && !isComingFromNonUserRoute && myBooks.length) {
      // Find most recently updated unordered book
      const unorderedBooks = myBooks.filter(book => !book.isOrderPlaces);
      if (unorderedBooks.length > 0) {
        const lastUpdatedBook = [...unorderedBooks].sort((a, b) =>
          new Date(b.updatedAt) - new Date(a.updatedAt)
        )[0];
        targetBookId = lastUpdatedBook._id;
      }
    }

    // Validate targetBookId has work in progress before setting as active
    if (targetBookId && !isComingFromNonUserRoute && window.location.pathname !== '/create-book') {
      const targetBook = myBooks.find(book => book._id === targetBookId);
      // Only set as active if book has work in progress
      if (targetBook && hasBookWorkInProgress(targetBook)) {
        activeBookId = targetBookId;
        dispatch(setActiveBookId({ bookId: targetBookId }));

        // Fetch images for active book and merge with pending localStorage images
        try {
          const batchImageRes = await fetchBatchImagesByBookId(targetBookId);
          const dbImages = batchImageRes?.docs?.data || [];

          const dbCoverImages = dbImages.filter((image) => !!image.isCover);
          if (dbCoverImages.length > 0) {
            const useStaging = await isStagingUploadEnabled();
            const covers = useStaging
              ? dbCoverImages
                  .map(mapDbCoverToGridImage)
                  .filter(Boolean)
                  .map((cover) => carryPendingCoverCropData(cover, targetBookId))
              : dbCoverImages;
            dispatch(setCoverImages(covers));
            // A pending record for a different photo is stale; one for this
            // photo still holds its crop rectangle, so keep it.
            if (!isSamePendingCover(loadPendingCoverImage(targetBookId), covers[0])) {
              clearPendingCoverImage(targetBookId);
            }
          } else {
            // No processed cover in DB yet — restore pending from localStorage
            const pendingCover = loadPendingCoverImage(targetBookId);
            if (pendingCover) {
              dispatch(setCoverImages([pendingCover]));
            }
          }

          const merged = await fetchOrderedInteriorImages(targetBookId, {
            // Reuse the rows above; if that request failed, this fetches again.
            rows: batchImageRes?.docs?.data,
          });
          dispatch(setImages(merged));
        } catch (error) {
          console.error('Error fetching book images:', error);
          // On fetch error, still try to load from localStorage
          const pendingImages = loadPendingImages(targetBookId);
          if (pendingImages.length > 0) {
            dispatch(setImages(pendingImages));
          }
          const pendingCover = loadPendingCoverImage(targetBookId);
          if (pendingCover) {
            dispatch(setCoverImages([pendingCover]));
          }
        }
      }
    } else {
      targetBookId = null
    }
  }

  // Store the full profile in Redux so downstream components can read from the store
  if (profile) {
    dispatch(setProfile(profile));
  }

  // A non-retried rejection (e.g. 401) also ends up here with no books. Only a real list response
  // makes `myBooks` authoritative enough to prune the shared cart against.
  const booksListed = bookRes?.docs?.success === true && Array.isArray(bookRes.docs.data);
  dispatch(setBooksLoaded(booksListed ? { userId, at: loadStartedAt } : null));
  dispatch(setBooksLoading(false));

  return { profile, myBooks, activeBookId };
};